import { useState, useEffect } from 'react'
import { Clock, CheckCircle, AlertTriangle, XCircle, ChevronRight, FileText, KeyRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import Header from '../components/layout/Header'
import { wildberriesAPI } from '../api/wildberries'
import { CheckHistoryItem } from '../types/wildberries'
import { useWbToken } from '../hooks/useWbToken'

const History = () => {
  const { isConnected } = useWbToken()
  const [items, setItems] = useState<CheckHistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedItem, setSelectedItem] = useState<CheckHistoryItem | null>(null)
  const [page, setPage] = useState(0)
  const limit = 20

  useEffect(() => {
    if (isConnected) {
      loadHistory()
    } else {
      setLoading(false)
    }
  }, [page, isConnected])

  const loadHistory = async () => {
    setLoading(true)
    try {
      const data = await wildberriesAPI.getCheckHistory(limit, page * limit)
      setItems(data.items || [])
      setTotal(data.total || 0)
    } catch (err) {
      console.error('Failed to load history:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-400'
    if (score >= 0.8) return 'text-success-500'
    if (score >= 0.5) return 'text-warning-500'
    return 'text-danger-500'
  }

  const getScoreBg = (score: number | null) => {
    if (score === null) return 'bg-gray-100'
    if (score >= 0.8) return 'bg-success-50'
    if (score >= 0.5) return 'bg-warning-50'
    return 'bg-danger-50'
  }

  const getComplianceIcon = (isCompliant: boolean | null) => {
    if (isCompliant === null) return <Clock className="w-5 h-5 text-gray-400" />
    if (isCompliant) return <CheckCircle className="w-5 h-5 text-success-500" />
    return <XCircle className="w-5 h-5 text-danger-500" />
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">История проверок</h1>
          <p className="mt-2 text-gray-600">
            Всего проверок: {total}
          </p>
        </div>

        {!isConnected ? (
          <div className="text-center py-20">
            <KeyRound className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Токен не подключён</h3>
            <p className="mt-2 text-gray-500">
              Подключите WB API-токен на дашборде, чтобы видеть историю проверок
            </p>
            <Link
              to="/dashboard"
              className="mt-6 inline-block px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
            >
              Перейти на дашборд
            </Link>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">История пуста</h3>
            <p className="mt-2 text-gray-500">
              Вы ещё не выполнили ни одной проверки товара
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center space-x-3 mb-2">
                        {getComplianceIcon(item.is_compliant)}
                        <span className="text-sm text-gray-500">
                          {formatDate(item.check_timestamp)}
                        </span>
                      </div>
                      <p className="text-gray-900 font-medium truncate">
                        {item.product_description.length > 100
                          ? item.product_description.substring(0, 100) + '...'
                          : item.product_description}
                      </p>
                      {item.summary && (
                        <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                          {item.summary}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center px-3 py-1 rounded-full ${getScoreBg(item.compliance_score)}`}>
                        <span className={`text-sm font-semibold ${getScoreColor(item.compliance_score)}`}>
                          {item.compliance_score !== null
                            ? `${(item.compliance_score * 100).toFixed(0)}%`
                            : 'N/A'}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center space-x-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Назад
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  {page + 1} из {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Вперёд
                </button>
              </div>
            )}
          </>
        )}

        {/* Модалка с деталями */}
        {selectedItem && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            onClick={() => setSelectedItem(null)}
          >
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black opacity-50" />
              <div
                className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Детали проверки</h2>
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Дата и статус */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">
                        {formatDate(selectedItem.check_timestamp)}
                      </span>
                    </div>
                    <div className={`flex items-center px-3 py-1 rounded-full ${getScoreBg(selectedItem.compliance_score)}`}>
                      <span className={`text-lg font-bold ${getScoreColor(selectedItem.compliance_score)}`}>
                        {selectedItem.compliance_score !== null
                          ? `${(selectedItem.compliance_score * 100).toFixed(0)}%`
                          : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Описание товара */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Описание товара
                    </h3>
                    <p className="text-gray-900 bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">
                      {selectedItem.product_description}
                    </p>
                  </div>

                  {/* Результат */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500">Статус:</span>
                    {selectedItem.is_compliant === true && (
                      <span className="flex items-center text-success-600 font-medium">
                        <CheckCircle className="w-5 h-5 mr-1" />
                        Соответствует требованиям
                      </span>
                    )}
                    {selectedItem.is_compliant === false && (
                      <span className="flex items-center text-danger-600 font-medium">
                        <AlertTriangle className="w-5 h-5 mr-1" />
                        Есть нарушения
                      </span>
                    )}
                    {selectedItem.is_compliant === null && (
                      <span className="flex items-center text-gray-500 font-medium">
                        <Clock className="w-5 h-5 mr-1" />
                        Не проверено
                      </span>
                    )}
                  </div>

                  {/* Резюме */}
                  {selectedItem.summary && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Резюме
                      </h3>
                      <p className="text-gray-700">{selectedItem.summary}</p>
                    </div>
                  )}

                  {/* Риски */}
                  {selectedItem.risks && selectedItem.risks.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Риски ({selectedItem.risks.length})
                      </h3>
                      <div className="space-y-2">
                        {selectedItem.risks.map((risk: any, idx: number) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg ${
                              risk.severity === 'высокий'
                                ? 'bg-danger-50 border border-danger-200'
                                : risk.severity === 'средний'
                                ? 'bg-warning-50 border border-warning-200'
                                : 'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">{risk.type}</span>
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                risk.severity === 'высокий'
                                  ? 'bg-danger-100 text-danger-700'
                                  : risk.severity === 'средний'
                                  ? 'bg-warning-100 text-warning-700'
                                  : 'bg-gray-200 text-gray-700'
                              }`}>
                                {risk.severity}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-600">{risk.description}</p>
                            {risk.source && (
                              <p className="mt-1 text-xs text-gray-400">{risk.source}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Рекомендации */}
                  {selectedItem.recommendations && selectedItem.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Рекомендации
                      </h3>
                      <ul className="space-y-2">
                        {selectedItem.recommendations.map((rec: string, idx: number) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default History